import React, { useEffect, useRef, useState } from "react";
import {
  useForm,
  SubmitHandler,
  FormProvider,
  Controller,
  UseFormReturn,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styles from "./index.module.scss";
import { Button } from "../../common/components/Button";
import { LocalizationProvider } from "@mui/x-date-pickers";
import CustomDatePicker from "../../common/components/DobTextfield";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DropdownWithInput from "../../common/components/DropdownTextfield";
import { cityList } from "../../utils/functions";
import { getUserInfoSchema } from "./formSchema";
import { useNavigate } from "react-router-dom";

interface IFormInput {
  firstName: string;
  lastName: string;
  email: string;
  dob: Date;
  city: string;
  otherCity?: string;
  location?: string;
  terms: boolean;
}

interface UserInfoFormProps {
  onFormSubmit?: (data: IFormInput) => void;
  onFormMethodsReady?: (methods: UseFormReturn<IFormInput>) => void;
}

const UserInfoForm: React.FC<UserInfoFormProps> = ({
  onFormSubmit,
  onFormMethodsReady
}) => {
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [showOtherField, setShowOtherField] = useState(false);

  const validationSchema = getUserInfoSchema();
  const methods = useForm<IFormInput>({
    resolver: yupResolver(validationSchema),
    mode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    unregister,
    control,
    watch,
  } = methods;

  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  // Pass form methods to parent component
  useEffect(() => {
    if (onFormMethodsReady) {
      onFormMethodsReady(methods);
    }
  }, [methods, onFormMethodsReady]);

  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector("input");
      if (firstInput) {
        firstInput.focus();
      }
    }
  }, []);

  useEffect(() => {
    setValue("terms", checked);
  }, [checked, setValue]);

  const cityValue = watch("city");

  useEffect(() => {
    setShowOtherField(cityValue === "Other");
    if (cityValue === "Other") {
      register("otherCity", { required: "Other City is required" });
    } else {
      unregister("otherCity");
    }
  }, [cityValue, register, unregister]);

  const citiesData = cityList.map((city) => ({
    value: city.name,
    label: city.name,
  }));

  const handleValueChange = (value: string) => {
    setValue("city", value);
    unregister("otherCity");
    clearErrors("city");
  };

  const handleDateChange = (date: Date | null) => {
    if (date !== null) {
      setValue("dob", date, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleOpenModal = (url: string) => {
    setIframeUrl(url);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setIframeUrl("");
  };

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    if (onFormSubmit) {
      onFormSubmit(data);
    }
  };

  function handleTakePicture(): void {
    navigate("/verifyuserface?updateProfilePicture=false");
  }

  function handleUploadPicture(): void {
    document.getElementById("upload-picture-input")?.click();
  }

  return (
    <FormProvider {...methods}>
      <form
        className={styles.formContainer}
        onSubmit={handleSubmit(onSubmit)}
        ref={formRef}
      >
        <div className={styles.userDetailsIntroContainer}>
          <p className={styles.userDetailsIntro}>
            {/* We'd love to get to know you a little better. */}
            {/* <br /> */}
            Please fill the following information:
          </p>
        </div>

        <div className={styles.inputGroup}>
          <div className={styles.formField}>
            <label htmlFor="firstName">First name</label>
            <input
              id="firstName"
              type="text"
              {...register("firstName")}
              className={errors.firstName ? styles.errorFields : ""}
            />
            {errors.firstName && (
              <i className={styles.error}>{errors.firstName.message}</i>
            )}
          </div>
          <div className={styles.formField}>
            <label htmlFor="lastName">Last name</label>
            <input
              id="lastName"
              type="text"
              {...register("lastName")}
              className={errors.lastName ? styles.errorFields : ""}
            />
            {errors.lastName && (
              <i className={styles.error}>{errors.lastName.message}</i>
            )}
          </div>
          <div className={styles.formField}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              {...register("email")}
              className={errors.email ? styles.errorFields : ""}
            />
            {errors.email && (
              <i className={styles.error}>{errors.email.message}</i>
            )}
          </div>
          <div className={styles.dobField}>
            <label htmlFor="dob">Date of Birth</label>
            <Controller
              name="dob"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <CustomDatePicker
                    value={field.value}
                    futureDate={false}
                    onChange={(date) => {
                      field.onChange(date);
                      handleDateChange(date);
                    }}
                    errorExist={!!errors.dob}
                    className={styles.dropdownInputFromEmail}
                    height="35px"
                    dobDate={true}
                  />
                </LocalizationProvider>
              )}
            />
            {errors.dob && (
              <i className={styles.error}>{errors.dob.message}</i>
            )}
          </div>
          <div className={styles.formField}>
            {/* <Controller
              name="city"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <DropdownWithInput
                  {...field}
                  label="Location*"
                  options={citiesData}
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleValueChange(value);
                  }}
                  error={errors.city?.message}
                  otherError={errors.otherCity?.message}
                  showOtherField={showOtherField}
                  height="35px"
                />
              )}
            /> */}
            <div className={styles.muiInputField}>
              <Controller
                name="city"
                control={control}
                defaultValue=""
                render={({ field: { value, onChange, ...field } }) => (
                  <DropdownWithInput
                    {...field}
                    value={value as string}
                    label="Location"
                    options={citiesData}
                    onValueChange={(value) => {
                      onChange(value);
                      handleValueChange(value);
                    }}
                    error={errors.city?.message}
                    otherError={errors.otherCity?.message}
                    showOtherField={showOtherField}
                    className={styles.dropdownInputStyle}
                    height="45px"
                    background="#fff"
                  />
                )}
              />

            </div>
            {showOtherField && (
              <div className={styles.formField}>
                <label htmlFor="otherCity">Other city name*</label>
                <input
                  id="otherCity"
                  type="text"
                  {...register("otherCity")}
                  className={errors.otherCity ? styles.errorFields : ""}
                />
                {errors.otherCity && (
                  <i className={styles.error}>{errors.otherCity.message}</i>
                )}
              </div>
            )}
          </div>
        </div>

        {/* <div className={styles.profilePictureSection}>
          <label className={styles.profilePictureLabel}>Profile picture</label>
          <div className={styles.pictureButtons}>
            <Button
              type="button"
              buttonClassName={styles.plainButton}
              buttonTextClassName={styles.pictureButtonText}
              datatestid="take-picture-btn"
              datatestidText="take-picture-btn-text"
              onClick={handleTakePicture}
            >
              Take picture
            </Button>
            <Button
              type="button"
              buttonClassName={styles.plainButton}
              buttonTextClassName={styles.pictureButtonText}
              datatestid="upload-picture-btn"
              datatestidText="upload-picture-btn-text"
              onClick={handleUploadPicture}
            >
              Upload picture
            </Button>
          </div>
          <div className={styles.note}>
            • File limit: 1MB
            <br />• Format: JPEG/PNG/PDF
          </div>
        </div> */}

        <div >
          <div className={styles.checkboxWrapper}>
            <input
              type="checkbox"
              id="terms"
              onChange={() => setChecked(!checked)}
              {...register("terms")}
            />
            <label htmlFor="terms">
              I have read and agreed to infinitheism's{" "}
              <a
                href="#"
                onClick={() =>
                  handleOpenModal(
                    `${process.env.REACT_APP_WEB_URL}termsAndConditionsV2.html`
                  )
                }
              >
                terms & conditions
              </a>{" "}
              and{" "}
              <a
                href="#"
                onClick={() =>
                  handleOpenModal(
                    `${process.env.REACT_APP_WEB_URL}privacyAndPolicy.html`
                  )
                }
              >
                privacy policy
              </a>
            </label>
          </div>
          {errors.terms && (
            <i className={styles.error}>{errors.terms.message}</i>
          )}
        </div>

        {/* Submit button removed - will be handled by parent */}
      </form>

      {/* Modal for terms/privacy */}
      {open && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <iframe
              src={iframeUrl}
              title="Terms and Policy"
              className={styles.DialogText}
              style={{
                width: "100%",
                height: "60vh",
                border: "none",
              }}
            />
            <Button onClick={handleCloseModal}>Close</Button>
          </div>
        </div>
      )}
    </FormProvider>
  );
};

export default UserInfoForm;